import React, {useContext, useState} from 'react';
import {Col, DatePicker, Form, Modal, Row, Select} from 'antd';
import "../../assets/css/Dentobook.css";
import "../../assets/css/PatientPhotos.css";
import {FormattedMessage, useIntl} from "react-intl";
import {CaretDownOutlined, CloseOutlined} from '@ant-design/icons';
import {
    Patient, InvoiceCreate, Organization, ToothTreatment
} from "../../api-client";
import {InvoicePaymentMethodEnum, InvoiceStatusEnum} from "../../types/calendarEnums";
import {isMobileOnly} from "react-device-detect";
import dayjs, { Dayjs } from 'dayjs';
import {AxiosResponse} from "axios";
import {AlertContext} from "../../context/AlertContext";
import {getOrganizationApi, getInvoicesApi} from "../../api/apiInstances";
import logger from "../../utils/logger";
import {ALERT_DURATIONS} from "../../constants/alerts";

interface PatientInvoiceCreateModalProps {
    patient: Patient;
    isModalOpen: boolean;
    setIsModalOpen: Function;
    loadData: Function;
    selectedTreatments: ToothTreatment[];
}

const PatientInvoiceCreateModal = ({
                                       loadData, isModalOpen, setIsModalOpen, patient, selectedTreatments
                                   }: PatientInvoiceCreateModalProps) => {

    const intl = useIntl();
    const {setVisible, setError,setSuccessMessage} = useContext(AlertContext);

    const [date, setDate] = useState<Dayjs | undefined>(dayjs());
    const [paymentDeadline, setPaymentDeadline] = useState<Dayjs | undefined>(dayjs().add(1, 'months'));
    const [paymentMethod, setPaymentMethod] = useState<InvoicePaymentMethodEnum>();
    const [vat, setVat] = useState<string>('20');
    const [status, setStatus] = useState<InvoiceStatusEnum>();
    const [loading, setLoading] = useState<boolean>(false);

    const organizationApi = getOrganizationApi();
    const invoicesApi = getInvoicesApi();

    const handleCancel = () => {
        setIsModalOpen(false);
    }

    const handleOk = async () => {
        try {
            await form.validateFields();

            setLoading(true);
            const organization: AxiosResponse<Organization> = await organizationApi.fetchOrganizationById(patient.organizationId!);

            const treatmentToothIds = selectedTreatments
                .map(treatment => treatment.id)
                .filter((id): id is string => id !== undefined);

            // Calculate the subtotal for all treatments (price + laboratory price)
            const calculateSubtotal = (selectedTreatments: ToothTreatment[]): number => {
                return selectedTreatments.reduce((acc, treatment) => {
                    const treatmentPrice = treatment.price!;
                    const laboratoryPrice = treatment.laboratoryPrice!;
                    return acc + (treatmentPrice + laboratoryPrice);
                }, 0);
            };


            // Calculate the total discount for all treatments
            const calculateTotalDiscount = (selectedTreatments: ToothTreatment[]): number => {
                return selectedTreatments.reduce((acc, treatment) => {
                    const treatmentPrice = treatment.price!;
                    const discount = treatment.discount!;
                    const discountAmount = treatmentPrice * (discount / 100);
                    return acc + discountAmount;
                }, 0);
            };

            // Calculate the subtotal
            const subtotal = calculateSubtotal(selectedTreatments);
            console.table(selectedTreatments);

            // Calculate the total discount
            const totalDiscount = calculateTotalDiscount(selectedTreatments);

            // Calculate the total after applying discounts but before VAT
            const totalBeforeVAT = selectedTreatments.reduce((acc, treatment) => {
                const treatmentPrice = treatment.price!;
                const discount = treatment.discount!;
                const laboratoryPrice = treatment.laboratoryPrice!;
                const treatmentTotal = treatmentPrice - (treatmentPrice * (discount / 100)) + laboratoryPrice;
                return acc + treatmentTotal;
            }, 0);

            // Calculate VAT
            let vatRate = 0.20;
            if (typeof vat === "string") {
                vatRate = parseFloat(vat) / 100;
            }

            // Calculate the VAT amount
            const vatAmount = totalBeforeVAT * vatRate;

            // Calculate the final total including VAT
            const total = totalBeforeVAT + vatAmount;

            const invoice: InvoiceCreate = {
                invoiceNumber: "",
                treatmentToothIds: treatmentToothIds,
                documentType: 'INVOICE',
                organizationId: patient.organizationId!,
                patientId: patient.id!,
                date: date?.toISOString()!,
                paymentDueDate: paymentDeadline?.toISOString()!,
                paymentMethod: paymentMethod!,
                cancelled: false,
                discount: totalDiscount,
                recipientAddress: patient.addressOne || "",
                recipientCity: patient.town || "",
                recipientEIK: patient.egn || "",
                recipientName: patient.firstName + ' ' + (patient.secondName ? (patient.secondName + ' ') : '') + patient.lastName,
                status: status ?? InvoiceStatusEnum.Unpaid,
                subtotal: subtotal,
                supplierAddress: organization.data.address || "",
                supplierCity: organization.data.town || "",
                supplierEIK: organization.data.uic || "",
                supplierName: organization.data.name || "",
                total: total,
                vat: vatRate * 100,
                vatAmount: vatAmount
            };
            logger.log("Invoice:", invoice);
            const savedInvoice = await invoicesApi.createInvoice(invoice);

            loadData();
            setSuccessMessage(intl.formatMessage({ id: 'success_message' }));
            setVisible(true);
            setTimeout(() => setVisible(false), ALERT_DURATIONS.STANDARD);
            setLoading(false);
            setIsModalOpen(false);

            // Download PDF with proper authentication
            const pdfResponse = await invoicesApi.generateInvoicePdf(savedInvoice.data.id!, { responseType: 'blob' });
            const blob = new Blob([pdfResponse.data], {type: 'application/pdf'});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Invoice_${savedInvoice.data.invoiceNumber}.pdf`;
            link.click();
            setTimeout(() => window.URL.revokeObjectURL(url), 100);
        } catch (err: any) {
            logger.error(err);
            setLoading(false);
            const blankFieldExists = Object.values(err.values).filter(item => !item).length > 0;

            if (blankFieldExists) {
                setError(intl.formatMessage({id: 'all_fields_required'}));
                setTimeout(() => setError(''), ALERT_DURATIONS.STANDARD);
            }
        }
    };

    const modalTitle = intl.formatMessage({id: 'create_invoice'});

    const [form] = Form.useForm();

    return (
        <Modal
            transitionName=""
            className={'dentobook-modal'}
            okButtonProps={{
                className: "modal-btn"
            }}
            cancelButtonProps={{
                className: "modal-btn"
            }}
            title={modalTitle}
            width={800}
            okText={<FormattedMessage id="create_button_text"/>}
            cancelText={<FormattedMessage id="cancel"/>}
            confirmLoading={loading}
            styles={{body: {overflowY: 'auto', maxHeight: 'calc(100vh - 300px)'}}}
            closeIcon={<CloseOutlined className={'icon-24px'} onClick={handleCancel}/>}
            open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
            <div style={{overflowY: 'auto', width: '100%', overflowX: 'hidden'}}>
                <Form layout="vertical" form={form}>
                    <Row gutter={24}>
                        <Col>
                            <Form.Item
                                className={"dentobook-form-item"}
                                label={<FormattedMessage id="date"/>}
                                name="date"
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                                initialValue={date}>
                                <DatePicker
                                    style={!isMobileOnly ? {width: 316} : {width: '88vw'}}
                                    format={"DD.MM.YYYY"}
                                    value={date}
                                    onChange={e => setDate(e)}
                                    placeholder=""/>
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item
                                className={"dentobook-form-item"}
                                label={<FormattedMessage id="payment_deadline"/>}
                                name="paymentDeadline"
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                                initialValue={paymentDeadline}>
                                <DatePicker
                                    style={!isMobileOnly ? {width: 316} : {width: '88vw'}}
                                    format={"DD.MM.YYYY"}
                                    value={paymentDeadline}
                                    onChange={e => setPaymentDeadline(e)}
                                    placeholder=""/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item
                        className={"dentobook-form-item input-full-width mt-16"}
                        label={<FormattedMessage id="payment_method"/>}
                        name="paymentMethod"
                        rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                        initialValue={''}
                    >
                        <Select
                            value={paymentMethod}
                            onChange={e => setPaymentMethod(e)}
                            className="dentobook-input"
                            showSearch
                            suffixIcon={<CaretDownOutlined/>}
                            optionFilterProp="label"
                        >
                            <Select.Option key="cash" value="CASH"><FormattedMessage
                                id="cash_payment_type"/></Select.Option>
                            <Select.Option key="card" value="CARD"><FormattedMessage
                                id="card_payment_type"/></Select.Option>
                            <Select.Option key="bank_transfer" value="BANK_TRANSFER"><FormattedMessage
                                id="bank_transfer"/></Select.Option>
                        </Select>
                    </Form.Item>
                    <Row gutter={24}>
                        <Col>
                            <Form.Item
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                                className={"dentobook-form-item input-full-width mt-16"}
                                label={<FormattedMessage id="vat_label"/>}
                                name="vat"
                                initialValue={vat}
                                style={!isMobileOnly ? {width: 316} : {width: '88vw'}}
                            >
                                <Select
                                    value={vat}
                                    onChange={e => setVat(e)}
                                    className="dentobook-input"
                                    showSearch
                                    suffixIcon={<CaretDownOutlined/>}
                                    optionFilterProp="label"
                                >
                                    <Select.Option key="zero" value="0">0%</Select.Option>
                                    <Select.Option key="nine" value="9">9%</Select.Option>
                                    <Select.Option key="twenty" value="20">20%</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col>
                            <Form.Item
                                rules={[{required: true, message: <FormattedMessage id="required_field"/>}]}
                                className={"dentobook-form-item input-full-width mt-16"}
                                label={<FormattedMessage id="status"/>}
                                name="status"
                                initialValue={''}
                            >
                                <Select
                                    onChange={e => setStatus(e)}
                                    value={status}
                                    className="dentobook-input"
                                    showSearch
                                    suffixIcon={<CaretDownOutlined/>}
                                    optionFilterProp="label"
                                    style={!isMobileOnly ? {width: 316} : {width: '88vw'}}
                                >
                                    <Select.Option key="paid" value="PAID"><FormattedMessage
                                        id="paid_invoice"/></Select.Option>
                                    <Select.Option key="unpaid" value="UNPAID"><FormattedMessage
                                        id="unpaid_invoice"/></Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </div>
        </Modal>
    );
};

export default PatientInvoiceCreateModal;